import { useState, useEffect, useRef, useCallback } from "react";
import { getOrdersDeliveryReady } from "../../api/orders";
import { OrderDetailedResumed } from "../../components/orderDetailedResumed";
import { orderDisplayNumber } from "../../utils/orderDisplayNumber";
import "./style.css";

export const OrdersDelivered = () => {
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(false);

  const query = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (mounted.current) setLoading(true);
    try {
      const response = await getOrdersDeliveryReady();
      // The existing endpoint uses this exact response for a confirmed empty assignment list.
      if (response?.error && response.message === "No orders assigned for the user") response.data = [];
      if (!response || (response.error && response.message !== "No orders assigned for the user") || !Array.isArray(response.data)) {
        throw new Error("Invalid delivery queue response");
      }
      if (mounted.current) {
        setData(response.data);
        setError(false);
      }
    } catch {
      if (mounted.current) setError(true);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    query();
    const interval = setInterval(query, 10000);
    return () => { mounted.current = false; clearInterval(interval); };
  }, [query]);

  const deliveries = data.filter(order => order.shippingMethod !== "Recogida en tienda");
  const active = deliveries.filter(order => order.status === "inDelivery");
  const history = deliveries.filter(order => order.status === "delivered");
  const queryText = search.trim().toLocaleLowerCase("es");
  const visibleHistory = history.filter(order => `${orderDisplayNumber(order)} ${order.Customer?.fullname || ""}`.toLocaleLowerCase("es").includes(queryText));

  return (
    <div className="ordersContainer delivery-list" aria-busy={loading}>
      <h1>Mis entregas</h1>
      <nav className="delivery-views" aria-label="Vista de entregas">
        <button type="button" aria-pressed={view === "active"} onClick={() => setView("active")}>En curso{!error && !loading ? ` (${active.length})` : ""}</button>
        <button type="button" aria-pressed={view === "history"} onClick={() => setView("history")}>Historial{!error && !loading ? ` (${history.length})` : ""}</button>
      </nav>
      {error ? (
        <div className="containerBlock">
          <p role="alert">No se pudieron cargar tus entregas. Comprueba tu conexión y vuelve a intentarlo. Si tu sesión ha caducado, inicia sesión de nuevo.</p>
          <button type="button" onClick={query} disabled={loading}>{loading ? "Reintentando…" : "Reintentar"}</button>
        </div>
      ) : loading && data.length === 0 ? (
        <p className="p-4" role="status">Cargando entregas…</p>
      ) : view === "active" ? (
        active.length === 0 ? <p className="p-4" role="status">No tienes entregas en curso</p> : active.map(order => <OrderDetailedResumed key={order.orderId} data={order} haveDetails={false} />)
      ) : <>
        <div className="delivery-history-search">
          <label htmlFor="delivery-history-search">Buscar en el historial</label>
          <input id="delivery-history-search" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Código de pedido o cliente" />
          <p>Pedidos entregados asignados a ti. Se muestran los más recientes primero.</p>
        </div>
        {visibleHistory.length === 0 ? <p className="p-4" role="status">{history.length ? "No hay entregas que coincidan con tu búsqueda" : "Todavía no tienes pedidos entregados"}</p> : visibleHistory.map(order => <HistoryOrder key={order.orderId} order={order} />)}
      </>}
    </div>
  );
};

const HistoryOrder = ({ order }) => {
  const date = new Date(order.updatedAt);
  const updated = Number.isNaN(date.getTime()) ? "Fecha no disponible" : date.toLocaleString("es-ES");
  const total = Number(order.amounts?.total);
  const amount = order.amounts?.total != null && Number.isFinite(total) ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(total) : "Importe no disponible";
  return <article className="containerBlock delivery-history-order" aria-label={`Pedido ${orderDisplayNumber(order)}`}>
    <header><strong>{orderDisplayNumber(order)}</strong><span>Entregado</span></header>
    <p>{order.Customer?.fullname || "Cliente sin nombre"}</p>
    <p>Última actualización: {updated}</p>
    <footer><strong>{amount}</strong><span>{order.paymentStatus === "paid" || order.paymentStatus === 1 ? "Pagado" : order.paymentStatus === "pending" ? "Pago pendiente" : "Estado de pago no disponible"}</span></footer>
  </article>;
};
